import type { ReactNode } from "react";
import { Text, Tooltip } from "@carbon6/c6-frontend-design";
import { appTextModes } from "../theme";
import styles from "./ServerInfoBadge.module.css";

export type ServerInfoBadgeEnvironment = "local" | "dev" | "prod";

export type ServerInfoBadgeState =
  | { status: "loading" }
  | { status: "failed" }
  | {
      status: "ready";
      environment: ServerInfoBadgeEnvironment;
      version: string;
    };

type ServerInfoBadgeProps = {
  collapsed: boolean;
  state: ServerInfoBadgeState;
  // 이 화면을 그리는 번들의 빌드 식별자다. 툴팁에만 표시한다. 배포된 FE 버전은 지원 대화에서
  // 묻는 값이고, 화면마다 읽을 값이 아니다.
  frontendBuildId?: string;
};

type ServerInfoBadgeView = {
  environment: ServerInfoBadgeEnvironment | "unknown";
  label: string;
  versionLabel: string | null;
  tooltip: string;
};

// 배포 컨텍스트는 모든 화면에서 브랜드 바로 아래에 같은 형태로 보인다. 펼친 상태에서는 환경과
// 짧은 commit을 함께 표시한다. 접힌 상태에서는 환경 마커만 표시한다. 전체 빌드 태그는 tooltip에 있다.
export function ServerInfoBadge({
  collapsed,
  state,
  frontendBuildId,
}: ServerInfoBadgeProps): ReactNode {
  const view = createServerInfoBadgeView(state, frontendBuildId);
  const className = collapsed ? `${styles.badge} ${styles.collapsed}` : styles.badge;

  return (
    <Tooltip
      className={collapsed ? styles.railTooltip : styles.tooltip}
      content={view.tooltip}
      placement="right"
    >
      <span
        className={className}
        data-environment={view.environment}
        data-status={state.status}
        role="status"
        aria-label={view.tooltip}
      >
        <span className={styles.dot} aria-hidden="true" />
        <Text
          variant={appTextModes["caption-bold"]}
          className={styles.environment}
          style={{ color: "inherit" }}
        >
          {collapsed ? collapsedLabel(view) : view.label}
        </Text>
        {!collapsed && view.versionLabel ? (
          <>
            <Text
              variant={appTextModes["caption-normal"]}
              className={styles.separator}
              style={{ color: "inherit" }}
              aria-hidden="true"
            >
              ·
            </Text>
            <Text variant={appTextModes["caption-normal"]} className={styles.version}>
              {view.versionLabel}
            </Text>
          </>
        ) : null}
      </span>
    </Tooltip>
  );
}

function createServerInfoBadgeView(
  state: ServerInfoBadgeState,
  frontendBuildId: string | undefined,
): ServerInfoBadgeView {
  switch (state.status) {
    case "loading":
      return {
        environment: "unknown",
        label: "SERVER",
        versionLabel: "확인 중",
        tooltip: "서버 환경과 버전을 확인하고 있습니다.",
      };
    case "failed":
      return {
        environment: "unknown",
        label: "SERVER",
        versionLabel: "UNKNOWN",
        tooltip: "서버 환경과 버전을 확인하지 못했습니다.",
      };
    case "ready": {
      const environmentLabel = state.environment.toUpperCase();
      const frontendLabel = frontendBuildId ? ` · FE 빌드: ${frontendBuildId}` : "";
      return {
        environment: state.environment,
        label: environmentLabel,
        versionLabel: shortVersion(state.version),
        tooltip: `서버 환경: ${environmentLabel} · BE 버전: ${state.version}${frontendLabel}`,
      };
    }
  }
}

function collapsedLabel(view: ServerInfoBadgeView): string {
  return view.environment === "unknown" ? "?" : view.label;
}

function shortVersion(version: string): string {
  const commit = /-g([0-9a-f]+)$/i.exec(version);
  return commit ? `g${commit[1]}` : version;
}
